import {CellItem} from "../../model/items/cellItem";
import {RadioItem} from "../../model/items/radio";
import RadioCell from "../shared/components/FormCell/RadioCell";
import {CheckboxItem} from "../../model/items/checkbox";
import CheckboxCell from "../shared/components/FormCell/CheckboxCell";
import {TextareaItem} from "../../model/items/textarea";
import TextareaCell from "../shared/components/FormCell/TextareaCell";
import {TextboxItem} from "../../model/items/textbox";
import TextboxCell from "../shared/components/FormCell/TextboxCell";
import {LabelItem} from "../../model/items/labelItem";
import LabelCell from "../shared/components/FormCell/LabelCell";
import {SliderItem} from "../../model/items/slider";
import SliderBox from "../shared/components/SliderBox/SliderBox";
import ICustomFormSettings from "../ICustomFormSettings";
import Cell from "../../model/form/cell";
import {Vnode} from "mithril";
import AudioPlayer from "../shared/components/MediaPlayer/AudioPlayer";
import LinkCell from "../shared/components/FormCell/LinkCell";
import {ImageItem} from "../../model/items/imageItem";
import Image from "../shared/components/Image/Image";
import ImageRow from "../shared/components/ImageRow/ImageRow";
import {VideoItem} from "../../model/items/video";
import Video from "../shared/components/Video/Video";
import NumericScaleItem from "../shared/components/NumericScaleItem/NumericScaleItem";
import {ComboItem} from "../../model/items/combo";
import ComboCell from "../shared/components/FormCell/ComboCell";
import SliderCell from "../shared/components/FormCell/SliderCell";

export default class GeneralCellFactory {
    constructor(
        protected readonly settings: ICustomFormSettings,
        protected readonly onSetValue: (cellItem: CellItem, value: any) => void,
    ) { }

    public fromRow(row: Cell[]): null|{view: ()=>Vnode|null}|undefined {
        if (row.length === 1) {
            return this.fromCellItem(row[0].cellItems[0]);
        }

        const type = String(this.getLabelFromCell(row[0])).toLowerCase();

        switch (type) {
            case "audio":
                return this.createAudioPlayer(row);
            case "link":
                return this.createLink(row);
            case "slider":
                return this.createSlider(row);
            case "images":
            case "imagerow":
                return this.createImageRow(row);
            case "nps":
                return this.createNpsItem(row);
            case "scale":
                return this.createNumericScaleItem(row);
            case "label":
            case "hint":
            case "radio":
            case "checkbox":
            case "combo":
            case "combobox":
            case "text":
            case "textbox":
            case "textarea":
            case "image":
            case "video":
                return this.fromCellItem(row[1].cellItems[0]);
            default:
                return this.fromCellItem(row[0].cellItems[0]);
        }
    }

    private getLabelFromCell(labelCell: Cell|undefined): string|undefined {
        const labelItem = (labelCell?.cellItems[0] instanceof LabelItem) ? labelCell.cellItems[0] : undefined;
        return labelItem?.questionary.renderText(labelItem?.label ?? '');
    }

    private fromCellItem(cellItem?: CellItem) {
        if (!cellItem) {
            return;
        }
        const cell = cellItem.cell;
        if (cellItem instanceof RadioItem) {
            return new RadioCell(cell,
                (ci, v) => this.onSetValue(ci, v),
                () => {
                    this.settings.nextForm();
                }
            );
        } else if (cellItem instanceof CheckboxItem) {
            return new CheckboxCell(cell, (ci, v) => this.onSetValue(ci, v),
                () => {
                    this.settings.nextForm();
                }
            );
        } else if (cellItem instanceof ComboItem) {
            return new ComboCell(cellItem);
        } else if (cellItem instanceof TextareaItem) {
            return new TextareaCell(cellItem)
        } else if (cellItem instanceof TextboxItem) {
            return new TextboxCell(cellItem)
        } else if (cellItem instanceof LabelItem) {
            return new LabelCell(cellItem);
        } else if (cellItem instanceof SliderItem) {
            return new SliderCell(cellItem);
        } else if (cellItem instanceof ImageItem) {
            return new Image(cellItem);
        } else if (cellItem instanceof VideoItem) {
            return new Video(cellItem);
        }
    }

    private createAudioPlayer(row: Cell[]): AudioPlayer {
        const validBox = <TextboxItem|undefined>(row[3])?.cellItems[0];
        const player = new AudioPlayer(
            this.getLabelFromCell(row[1]) || '',
            JSON.parse(this.getLabelFromCell(row[2]) || '{}'),
            () => {
                validBox?.set(1);
                player.enableSeeker();
            }
        );
        return player;
    }

    private createLink(row: Cell[]): LinkCell {
        const label = <LabelItem|undefined>(row[1])?.cellItems[0] ?? <LabelItem>(row[0])?.cellItems[0];
        const url = (this.getLabelFromCell(row[2]) || '').replace(/^(\s|\u202F|\u00A0)+|(\s|\u202F|\u00A0)+$/g, '');
        const validBox = <CheckboxItem|undefined>(row[3])?.cellItems[0];
        return new LinkCell(label, url || '', () => validBox?.setVariable(true));
    }

    /**
     * SE2 or SE3 slider
     * When slider got extra params it is rendered as SE3 slider
     */
    private createSlider(row: Cell[]): SliderBox|{view: ()=>Vnode|null}|null {
        const slider = row[1].cellItems[0];
        if (!(slider instanceof SliderItem)) {
            console.log('customForm-General: Unable to initialize slider from row. Expect: "slider", SliderItem, ["numeric"|"numeric-labeled"|"text"|"auto"], [Image]');
            return null;
        }
        const leftLabel = slider.labels.left;
        const rightLabel = slider.labels.right;
        const typeLabel = <undefined|LabelItem>row[2]?.cellItems[0];
        const image = <undefined|ImageItem>row[3]?.cellItems[0];

        if ((typeLabel && !(typeLabel instanceof LabelItem)) ||
            (image && !(image instanceof ImageItem))
        ) {
            console.log('customForm-General: Unable to initialize slider from row. Expect: "slider", SliderItem, ["numeric"|"numeric-labeled"|"text"|"auto"], [Image]');
            return null;
        }
        const imageUrl = image?.file;

        // Return SE2 slider
        if (!typeLabel && !imageUrl) {
            return new SliderCell(slider);
        }

        // Return SE3 slider
        return new SliderBox(
            slider,
            leftLabel,
            rightLabel,
            <'numeric'|'numeric-labeled'|'text'|'auto'>this.getLabelFromCell(row[2]) || 'auto',
            imageUrl ? this.settings.form.questionary.getFileUrl(imageUrl) : undefined
        );
    }

    private createImageRow(row: Cell[]): ImageRow|null {
        const imageItems = <ImageItem[]>row.map(cell => cell.getItems()[0]).filter(ci => ci instanceof ImageItem);
        if (imageItems.length === 0) {
            console.log("customForm-General: Unable to initialize imageRow. No image found in this row.");
            return null;
        }
        return new ImageRow(imageItems);
    }

    private createNpsItem(row: Cell[]): NumericScaleItem|null {
        const radioItems = <RadioItem[]>row.map(cell => cell.getItems()[0]).filter(ci => ci instanceof RadioItem);
        const labels = <LabelItem[]>row.map(cell => cell.getItems()[0]).filter((ci, i) => i!==0 && ci instanceof LabelItem);
        if (radioItems.length < 10 || radioItems.length > 12) {
            console.log(`customForm-General: Unable to initialize nps. Expect 10-12 radioItems, got ${radioItems.length}.`);
            return null;
        }

        return new NumericScaleItem(radioItems, labels, '6');
    }

    private createNumericScaleItem(row: Cell[]): NumericScaleItem|null {
        const radioItems = <RadioItem[]>row.map(cell => cell.getItems()[0]).filter(ci => ci instanceof RadioItem);
        const labels = <LabelItem[]>row.map(cell => cell.getItems()[0]).filter((ci, i) => i!==0 && ci instanceof LabelItem);
        if (radioItems.length < 2) {
            console.log(`customForm-General: Unable to initialize scale. Expect more than 2 radioItems, got ${radioItems.length}.`);
            return null;
        }

        return new NumericScaleItem(radioItems, labels);
    }
}
